import { type IETFLanguageTag } from '@opallabs/concepts'

/** Reads a scheduled instant as the time a chip shows. */
export type ChipTimeFormat = (scheduledAt: string) => string

/**
 * Builds the time a chip shows, as short as the clock allows.
 *
 * A day cell has room for a few words, so a time on the hour drops its
 * minutes: `3 PM` rather than `3:00 PM`. A clock with no AM or PM keeps them —
 * `15` on its own reads as an hour, not as three in the afternoon — so the
 * minutes only go where a day period is there to take their place. The day
 * period sits against the time, `3:30PM`, because the space before it cost a
 * narrow row the end of the time itself.
 *
 * The formatter is built once and used for every chip of a month: building one
 * per item is the expensive part of `Intl`.
 */
export const createChipTimeFormat = (
  locale?: IETFLanguageTag
): ChipTimeFormat => {
  const formatter = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
  })

  return (scheduledAt) => {
    const parts = formatter.formatToParts(new Date(scheduledAt))
    const minute = parts.findIndex((part) => part.type === 'minute')
    const isDropped =
      minute > 0 &&
      Number(parts[minute].value) === 0 &&
      parts[minute - 1].type === 'literal' &&
      parts.some((part) => part.type === 'dayPeriod')

    return parts
      .filter(
        // The separator in front of the minutes goes with them.
        (_part, index) =>
          !isDropped || (index !== minute && index !== minute - 1)
      )
      .filter(
        (part, index, kept) =>
          !(
            part.type === 'literal' &&
            part.value.trim() === '' &&
            kept[index + 1]?.type === 'dayPeriod'
          )
      )
      .map((part) => part.value)
      .join('')
  }
}
